import { useCallback, useMemo } from 'react';
import { DataResponse } from '@/core';
import { useDataQueryResult } from '../DataQueriesProvider';
import { useTimeRange } from '../TimeRangeProviderV2';
import { DataSnapshotContext, DataSnapshotContextValue, useDataSnapshotContext } from './DataSnapshotContext';
import { DataSnapshot } from './model';

export interface DataSnapshotProviderProps extends DataSnapshotContextValue {
  children?: React.ReactNode;
}

export function DataSnapshotProvider({ children, storeSnapshot, getSnapshot }: DataSnapshotProviderProps) {
  const value = useMemo(() => ({ storeSnapshot, getSnapshot }), [storeSnapshot, getSnapshot]);
  return <DataSnapshotContext.Provider value={value}>{children}</DataSnapshotContext.Provider>;
}

/**
 * Hook to create data snapshots. Must be used within a DataQueriesProvider.
 * If no DataSnapshotProvider is present in the tree, returns null.
 *
 * Returns a `createSnapshot` callback that captures the current query results
 * and stores them via the provider's `storeSnapshot` method.
 */
export function useDataSnapshot() {
  const snapshotContext = useDataSnapshotContext();
  const { queryResults, isLoading, isFetching, isFetchingMore } = useDataQueryResult();
  const { absoluteTimeRange } = useTimeRange();

  const queryResultsAreFinished = !isLoading && !isFetching && !isFetchingMore;

  const createSnapshot = useCallback(async () => {
    if (!snapshotContext) {
      throw new Error('Cannot create a snapshot without a DataSnapshotProvider');
    }

    const snapshot: DataSnapshot = {
      results: queryResults.map(({ type, data }) => ({
        dataResponse: { type, data } as DataResponse,
        version: 1, // TODO: This should be replaced with version number from the plugin once it is available
      })),
      metadata: {
        timeRange: absoluteTimeRange,
      },
    };

    return snapshotContext.storeSnapshot(snapshot);
  }, [queryResults, snapshotContext, absoluteTimeRange]);

  const response = useMemo(() => {
    if (snapshotContext !== undefined && queryResultsAreFinished) {
      return {
        createSnapshot,
      };
    }

    return null;
  }, [createSnapshot, queryResultsAreFinished, snapshotContext]);

  return response;
}
